import { UIPaths } from '@consts';
import { useGoalsService } from '@hooks';
import { LoaderIcon } from '@icons';
import {
  GoalCardLinkTarget,
  GoalGetFiltersModel,
  GoalModel,
  GoalSubscriptionModel,
  GoalType,
  ToastType,
  VersionedEntityType
} from '@models';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { BasicGoalSubscriptionModel } from 'lib/models/shared';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import GoalCard from './GoalCard';

// Define keyframe animations globally to avoid ESLint jsx property error
const animationStyles = `
  @keyframes fadeSlideDown {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fadeSlideUp {
    to {
      opacity: 0;
      transform: translateY(-20px);
    }
  }
`;

export default function GoalCardComposite({
  goal,
  showActions,
  goalSubscription,
  linkTarget = GoalCardLinkTarget.GOAL,
  goalsFilters
}: {
  goal: GoalModel;
  showActions: {
    versionType?: boolean;
    deadline?: boolean;
    priority?: boolean;
    like?: boolean;
    conversation?: boolean;
    share?: boolean;
    more?: boolean;
    edit?: boolean;
  };
  goalSubscription?: GoalSubscriptionModel | BasicGoalSubscriptionModel | null;
  linkTarget: GoalCardLinkTarget;
  goalsFilters?: GoalGetFiltersModel;
}) {
  const [showChildGoals, setShowChildGoals] = useState(false);
  const [childGoals, setChildGoals] = useState<GoalModel[] | null>(null);
  const { getGoals } = useGoalsService();
  const [loading, setLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const childrenContainerRef = useRef<HTMLDivElement>(null);
  const firstChildRef = useRef<HTMLDivElement>(null);
  const { setToast } = useStore(
    (state) => ({
      setToast: state.setToast
    }),
    shallow
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (
      showChildGoals &&
      !loading &&
      childGoals &&
      childGoals.length > 0 &&
      firstChildRef.current
    ) {
      setTimeout(() => {
        firstChildRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300); // Wait for animation to start before scrolling
    }
  }, [showChildGoals, loading, childGoals]);

  const onShowChildGoals = (showChildGoals: boolean) => {
    if (goal.type !== GoalType.COMBO) {
      return;
    }
    if (showChildGoals) {
      setIsExiting(false);
      setShowChildGoals(true);
      if (_.isNil(childGoals)) {
        // Lazy load children
        setLoading(true);
        getGoals({
          comboGoalEntityId: goal.entityId,
          maxVersionType: VersionedEntityType.PUBLISHED,
          filters: goalsFilters
        })
          .then((goalsResponse) => {
            setChildGoals(goalsResponse.goals);
          })
          .catch((error) => {
            setToast({
              message: t('domains.goals.couldNotReadGoals'),
              type: ToastType.ERROR
            });
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      setIsExiting(true);
      // Delay hiding to allow exit animation to complete
      setTimeout(() => {
        setShowChildGoals(false);
        setIsExiting(false);
      }, 400); // Duration matches the animation plus a small buffer
    }
  };

  if (goal.type !== GoalType.COMBO) {
    return (
      <GoalCard
        goal={goal}
        showActions={showActions}
        goalSubscription={goalSubscription}
        customLink={
          linkTarget === GoalCardLinkTarget.SUBSCRIPTION && goalSubscription
            ? UIPaths.goalSubscription(goalSubscription.id)
            : UIPaths.goal(goal.entityId)
        }
      />
    );
  }

  return (
    <div
      className={classNames('w-full flex flex-col gap-sm', setupForDark('border-dark'), {
        'border-r-[3px] rounded-r-lg pr-xs': showChildGoals
      })}
    >
      <GoalCard
        goal={goal}
        showActions={showActions}
        goalSubscription={goalSubscription}
        customLink={
          linkTarget === GoalCardLinkTarget.SUBSCRIPTION && goalSubscription
            ? UIPaths.goalSubscription(goalSubscription.id)
            : UIPaths.goal(goal.entityId)
        }
        onShowChildGoals={onShowChildGoals}
      />
      {(showChildGoals || isExiting) && (
        <div ref={childrenContainerRef} className='pl-huge flex flex-col gap-sm overflow-hidden'>
          {loading && <LoaderIcon className='mx-auto' />}
          {!loading &&
            childGoals?.map((childGoal, index) => (
              <div
                key={childGoal.entityId}
                ref={index === 0 ? firstChildRef : null}
                className={`transform transition-all duration-300 ease-out ${isExiting ? 'exit-animation' : ''}`}
                style={{
                  opacity: isExiting ? 1 : 0,
                  transform: isExiting ? 'translateY(0)' : 'translateY(-20px)',
                  animation: isExiting
                    ? `fadeSlideUp 0.3s ease-out forwards ${(childGoals.length - 1 - index) * 0.1}s`
                    : `fadeSlideDown 0.3s ease-out forwards ${index * 0.1}s`
                }}
              >
                <GoalCard
                  orderedLabel={index + 1}
                  goal={childGoal}
                  showActions={showActions}
                  goalSubscription={childGoal.subscription}
                  customLink={
                    linkTarget === GoalCardLinkTarget.SUBSCRIPTION && childGoal.subscription
                      ? UIPaths.goalSubscription(childGoal.subscription.id)
                      : UIPaths.goal(childGoal.entityId)
                  }
                />
              </div>
            ))}
          <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
        </div>
      )}
    </div>
  );
}
